import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { FormikHelpers, useFormik } from "formik";
import Dialog from "@mui/material/Dialog";
import {
  callDynamicFormApi,
  getDynamicFormInitialValues,
  getDynamicFormValidationSchema,
  renderFormFields,
} from "./helper";
import CircularProgress from "@mui/material/CircularProgress";
import {
  showErrorSnackbar,
  showSuccessSnacbar,
} from "@components/shared-layouts/Snackbar/helper";
import ProgressbarModal from "./ProgressbarModal";
import OperationSuccessfullyCompletedModal from "./OperationSuccessfullyCompletedModal";
import { useParams } from "react-router-dom";
import ProjectContext from "@context/Project/projectContext";

interface DynamicFormProps {
  page: string;
  handleFieldChange?: (fieldName: string, fieldValue: any) => void;
}

function DynamicForm({ page, handleFieldChange }: DynamicFormProps) {
  const navigate = useNavigate();

  const [dialogOpenStatus, setDialogOpenStatus] = useState<boolean>(false);

  const [operationSuccessModal, setOperationSuccessModal] =
    useState<boolean>(false);

  const [checkLiveStatusModal, setCheckLiveStatusModal] =
    useState<boolean>(false);

  const { projectId, project_type } = useParams();

  const projectContextInfo = useContext(ProjectContext);
  const { state: projectState, setState: setProjectState } = projectContextInfo;

  const handleDatalabellingSelectionResult = (values: any, res: any) => {
    const _labels = res?.data?.classes ?? [];
    const _imageIds = res?.data?.image_ids ?? [];
    const _colors = res?.data?.colors ?? [];
    const _src_dataset = values?.src_dataset;
    setProjectState({
      ...projectState,
      labels: _labels,
      colors: _colors,
      imageIds: _imageIds,
      src_dataset: _src_dataset,
    });
    navigate(
      `/${project_type}/operations/${projectId}/data-labelling/selection?fullScreen=true`
    );
  };

  const handleSubmit = (values: any, actions: FormikHelpers<any>) => {
    values["project_id"] = projectId;
    const apiToCall = callDynamicFormApi(page);

    apiToCall({ ...values, project_type })
      .then((x: any) => {
        actions.resetForm();
        actions.setSubmitting(false);
        if (page === "dataLabellingSelection") {
          handleDatalabellingSelectionResult(values, x);
          return;
        }
        setProjectState({
          ...projectState,
          processId: x.process_id,
        });
        setOperationSuccessModal(true);
        showSuccessSnacbar(x.message);
      })
      .catch((e: any) => {
        if (e.status === 400) {
          showErrorSnackbar(e.error.message);
        }
        actions.setSubmitting(false);
      });
  };

  const formik = useFormik({
    initialValues: getDynamicFormInitialValues(page),
    validationSchema: getDynamicFormValidationSchema(page, project_type),
    onSubmit: handleSubmit,
  });

  const { handleSubmit: handleSub, isSubmitting } = formik || {};

  const handleDialogClose = () => {
    setDialogOpenStatus(false);
  };

  const handleOperationModalClose = () => {
    setOperationSuccessModal(false);
  };

  const handleCheckLiveStatusModalClose = () => {
    setCheckLiveStatusModal(false);
  };

  const handleCheckLiveStatus = () => {
    setOperationSuccessModal(false);
    setCheckLiveStatusModal(true);
  };

  const renderFormFieldsValues = {
    showModalFields: false,
    page: page,
    formik: formik,
    handleFieldChange: handleFieldChange,
    setDialogOpenStatus: setDialogOpenStatus,
  };

  return (
    <>
      <form onSubmit={handleSub} style={{ display: "inline-grid" }}>
        {renderFormFields(renderFormFieldsValues)}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <CircularProgress color="primary" size={20} />
              <p style={{ margin: "0px 10px" }}>Starting</p>
            </>
          ) : (
            "Start"
          )}
        </Button>

        <Dialog
          open={dialogOpenStatus}
          onClose={handleDialogClose}
          fullWidth={true}
        >
          <Box sx={{ p: 2, display: "inline-grid" }}>
            <h4>Change Default Settings</h4>
            <div style={{ display: "inline-grid", marginTop: "10px" }}>
              {renderFormFields({
                ...renderFormFieldsValues,
                showModalFields: true,
              })}
            </div>
            <Button
              variant="contained"
              onClick={() => handleDialogClose()}
              color="primary"
              style={{ maxWidth: "100px" }}
            >
              Done
            </Button>
          </Box>
        </Dialog>

        <OperationSuccessfullyCompletedModal
          open={operationSuccessModal}
          close={handleOperationModalClose}
          handleCheckLiveStatus={handleCheckLiveStatus}
        />
        {checkLiveStatusModal && (
          <ProgressbarModal
            page={page}
            open={checkLiveStatusModal}
            close={handleCheckLiveStatusModalClose}
          />
        )}
      </form>
    </>
  );
}

export default React.memo(DynamicForm);
